'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { BookOpen, ChevronDown, Shield, TrendingUp, Building2, Landmark, BarChart3, Eye, Settings, CheckCircle, AlertTriangle, Download, ArrowRight, Flag, Hash, Target, Monitor, Activity, Send, Info, X, Menu, ChevronUp, ExternalLink, Zap } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────
interface Section {
  id: string;
  title: string;
  content: React.ReactNode;
}

interface UserRole {
  id: string;
  label: string;
  icon: React.ElementType;
  color: string;
  description: string;
  portalUrl: string;
  sections: Section[];
}

// ─── Reusable Doc Components ──────────────────────────────────────────────────
function StepList({ steps }: { steps: { num: number; title: string; desc: string }[] }) {
  return (
    <ol className="space-y-3 mt-3">
      {steps.map((s) => (
        <li key={s.num} className="flex gap-3">
          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-accent/20 text-accent text-xs font-bold flex items-center justify-center mt-0.5">{s.num}</span>
          <div>
            <p className="text-sm font-semibold text-foreground">{s.title}</p>
            <p className="text-xs text-muted mt-0.5">{s.desc}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function FeatureTable({ rows }: { rows: { feature: string; description: string; access: string }[] }) {
  return (
    <div className="overflow-x-auto mt-3">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-2 pr-4 text-muted font-semibold uppercase tracking-wider">Feature</th>
            <th className="text-left py-2 pr-4 text-muted font-semibold uppercase tracking-wider">Description</th>
            <th className="text-left py-2 text-muted font-semibold uppercase tracking-wider">Access Level</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/50">
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-surface-2/50">
              <td className="py-2 pr-4 font-medium text-foreground whitespace-nowrap">{r.feature}</td>
              <td className="py-2 pr-4 text-muted">{r.description}</td>
              <td className="py-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  r.access === 'Read-Only' ? 'bg-info/10 text-info' :
                  r.access === 'Full Access' ? 'bg-positive/10 text-positive' :
                  r.access === 'Action Required'? 'bg-warning/10 text-warning' : 'bg-accent/10 text-accent'
                }`}>{r.access}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UATScenario({ id, title, preconditions, steps, expected, status }: {
  id: string; title: string; preconditions: string[]; steps: string[]; expected: string; status: 'pass' | 'fail' | 'pending';
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-lg overflow-hidden mb-2">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-surface-2 hover:bg-surface-2/80 text-left"
      >
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono text-muted">{id}</span>
          <span className="text-sm font-medium text-foreground">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            status === 'pass' ? 'bg-positive/10 text-positive' :
            status === 'fail'? 'bg-critical/10 text-critical' : 'bg-warning/10 text-warning'
          }`}>{status === 'pass' ? '✓ Pass' : status === 'fail' ? '✗ Fail' : '⏳ Pending'}</span>
          {open ? <ChevronUp size={14} className="text-muted" /> : <ChevronDown size={14} className="text-muted" />}
        </div>
      </button>
      {open && (
        <div className="px-4 py-3 bg-surface border-t border-border space-y-3">
          <div>
            <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-1">Pre-conditions</p>
            <ul className="list-disc list-inside space-y-0.5">
              {preconditions.map((p, i) => <li key={i} className="text-xs text-muted">{p}</li>)}
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-1">Test Steps</p>
            <ol className="list-decimal list-inside space-y-0.5">
              {steps.map((s, i) => <li key={i} className="text-xs text-foreground">{s}</li>)}
            </ol>
          </div>
          <div>
            <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-1">Expected Result</p>
            <p className="text-xs text-positive bg-positive/5 border border-positive/20 rounded px-3 py-2">{expected}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoBox({ type, children }: { type: 'info' | 'warning' | 'tip'; children: React.ReactNode }) {
  const cfg = {
    info: { bg: 'bg-info/5 border-info/20 text-info', icon: Info },
    warning: { bg: 'bg-warning/5 border-warning/20 text-warning', icon: AlertTriangle },
    tip: { bg: 'bg-positive/5 border-positive/20 text-positive', icon: CheckCircle },
  }[type];
  const Icon = cfg.icon;
  return (
    <div className={`flex gap-2 p-3 rounded-lg border mt-3 ${cfg.bg}`}>
      <Icon size={14} className="flex-shrink-0 mt-0.5" />
      <p className="text-xs">{children}</p>
    </div>
  );
}

// ─── Role Data ────────────────────────────────────────────────────────────────
const roles: UserRole[] = [
  // ── INVESTOR ──────────────────────────────────────────────────────────────
  {
    id: 'investor',
    label: 'Investor',
    icon: TrendingUp,
    color: 'text-accent',
    description: 'Accredited retail, sophisticated, or institutional investor accessing campaigns, managing portfolio, and tracking distributions.',
    portalUrl: '/',
    sections: [
      {
        id: 'inv-overview',
        title: 'Role Overview',
        content: (
          <div className="space-y-3">
            <p className="text-sm text-muted">The Investor role provides access to the full investment lifecycle — from KYC onboarding through campaign discovery, commitment, portfolio monitoring, and distribution receipt. Investors are categorised as Retail, Sophisticated, Professional, or Institutional, each with different investment limits and eligibility rules.</p>
            <FeatureTable rows={[
              { feature: 'Investor Dashboard', description: 'Portfolio overview, unrealised gains, active investments, KYC status', access: 'Full Access' },
              { feature: 'Campaign Discovery', description: 'Browse, filter, and view live investment campaigns', access: 'Full Access' },
              { feature: 'Investment Commitment', description: 'Multi-step commitment flow with eligibility checks and escrow funding', access: 'Full Access' },
              { feature: 'KYC Submission', description: 'Submit identity documents, liveness check, suitability questionnaire', access: 'Full Access' },
              { feature: 'KYC Status', description: 'Track KYC/KYB verification progress in real time', access: 'Read-Only' },
              { feature: 'Document Vault', description: 'Access investment agreements, receipts, and disclosure documents', access: 'Read-Only' },
              { feature: 'Investor Account', description: 'Manage profile, compliance status, and account settings', access: 'Full Access' },
              { feature: 'Notification Preferences', description: 'Configure email/in-app alerts for portfolio events', access: 'Full Access' },
            ]} />
          </div>
        ),
      },
      {
        id: 'inv-onboarding',
        title: 'Onboarding & KYC',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">All investors must complete KYC/KYB before committing to any investment. Navigate to <strong className="text-foreground">Account → KYC / Suitability</strong> to begin.</p>
            <StepList steps={[
              { num: 1, title: 'Select Investor Type', desc: 'Choose Individual or Institutional. This determines which document set is required.' },
              { num: 2, title: 'Complete Personal / Entity Profile', desc: 'Enter full legal name, date of birth, nationality, address, and contact details.' },
              { num: 3, title: 'Upload Identity Documents', desc: 'Passport or national ID (front + back). Institutional investors upload Certificate of Incorporation, Memorandum & Articles, and Board Resolution.' },
              { num: 4, title: 'Liveness Check (Individual only)', desc: 'Complete the real-time facial biometric scan. Ensure good lighting and a plain background.' },
              { num: 5, title: 'UBO Declaration (Institutional only)', desc: 'Declare all Ultimate Beneficial Owners with >25% ownership. Each UBO requires name, nationality, DOB, and PEP status.' },
              { num: 6, title: 'Suitability Questionnaire', desc: 'Answer questions on investment experience, risk appetite, and financial situation. Results determine your investor tier.' },
              { num: 7, title: 'Submit for Review', desc: 'Review all entries and submit. You will receive an email confirmation. Typical review time: 1–3 business days.' },
            ]} />
            <InfoBox type="warning">KYC must be approved before you can commit to any investment. You can browse campaigns while KYC is pending, but the "Commit" button will be locked.</InfoBox>
          </div>
        ),
      },
      {
        id: 'inv-campaigns',
        title: 'Browsing & Committing to Campaigns',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">Navigate to <strong className="text-foreground">Opportunities → Browse Campaigns</strong> to view all live investment opportunities.</p>
            <StepList steps={[
              { num: 1, title: 'Filter Campaigns', desc: 'Use the filter panel to narrow by asset class, risk level, IRR range, tenor, and jurisdiction.' },
              { num: 2, title: 'Review Campaign Detail', desc: 'Click any campaign card to view the full prospectus, funding progress, Project Director profile, documents, and investor ratings.' },
              { num: 3, title: 'Check Eligibility', desc: 'Each campaign shows your eligibility status (Eligible / Not Eligible / Amount Restricted). Hover the badge for details.' },
              { num: 4, title: 'Initiate Commitment', desc: 'Click "Commit to Investment" on the campaign detail page or navigate to Opportunities → Commit to Investment.' },
              { num: 5, title: 'Eligibility & Limit Checks', desc: 'The system automatically checks KYC status, investment limits, and portfolio concentration rules.' },
              { num: 6, title: 'Select Funding Method', desc: 'Choose from Bank Wire, ACH, SWIFT, or Crypto. Review processing times and fees.' },
              { num: 7, title: 'Review Allocation Impact', desc: 'See how this investment changes your portfolio allocation across asset classes.' },
              { num: 8, title: 'Escrow Confirmation', desc: 'Funds are held in escrow until the campaign closes. You receive a cryptographically-signed receipt.' },
              { num: 9, title: 'Confirmation & Audit Trail', desc: 'A full audit trail of your commitment is recorded. Download your investment receipt from Document Vault.' },
            ]} />
            <InfoBox type="info">Minimum investment amounts vary by campaign. Retail investors are subject to annual investment limits under applicable regulations.</InfoBox>
          </div>
        ),
      },
      {
        id: 'inv-portfolio',
        title: 'Portfolio Monitoring',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">The Investor Dashboard provides a real-time view of your portfolio performance.</p>
            <FeatureTable rows={[
              { feature: 'Overview Tab', description: 'Total invested, unrealised gain/loss, distributions received, pending commitments', access: 'Read-Only' },
              { feature: 'Investments Tab', description: 'List of all active and historical investments with status badges', access: 'Read-Only' },
              { feature: 'Gains Tab', description: 'Portfolio value chart and allocation breakdown by asset class', access: 'Read-Only' },
              { feature: 'Campaigns Tab', description: 'Live campaigns you have committed to with funding progress', access: 'Read-Only' },
              { feature: 'Documents Tab', description: 'All documents associated with your investments', access: 'Read-Only' },
            ]} />
            <InfoBox type="tip">Use the real-time alert panel (bell icon, top right) to receive instant notifications on portfolio events, distribution payments, and compliance updates.</InfoBox>
          </div>
        ),
      },
      {
        id: 'inv-uat',
        title: 'UAT Test Scenarios',
        content: (
          <div className="space-y-2">
            <UATScenario
              id="INV-UAT-001"
              title="Individual KYC Submission — Happy Path"
              preconditions={['User is registered and logged in', 'KYC status is "Not Started"']}
              steps={[
                'Navigate to Account → KYC / Suitability',
                'Select "Individual" investor type',
                'Complete Personal Info form with valid data',
                'Upload valid passport (front + back)',
                'Complete liveness check successfully',
                'Complete suitability questionnaire',
                'Review and submit application',
              ]}
              expected="Application submitted successfully. Status changes to 'Under Review'. Confirmation email received."
              status="pending"
            />
            <UATScenario
              id="INV-UAT-002"
              title="Investment Commitment — Eligible Investor"
              preconditions={['KYC status is "Approved"', 'At least one live campaign exists', 'Investor tier is Sophisticated or above']}
              steps={[
                'Navigate to Campaign Discovery',
                'Select a campaign with "Eligible" badge',
                'Click "Commit to Investment"',
                'Verify eligibility checks all pass',
                'Enter commitment amount above minimum',
                'Select Bank Wire as funding method',
                'Review allocation impact',
                'Confirm commitment',
              ]}
              expected="Commitment recorded. Escrow receipt generated. Investment appears in Dashboard under Investments tab."
              status="pending"
            />
            <UATScenario
              id="INV-UAT-003"
              title="Investment Blocked — KYC Pending"
              preconditions={['User is registered', 'KYC status is "Pending" or "Not Started"']}
              steps={[
                'Navigate to Campaign Discovery',
                'Select any live campaign',
                'Attempt to click "Commit to Investment"',
              ]}
              expected="Commit button is disabled or shows KYC required message. User is redirected to KYC submission."
              status="pending"
            />
            <UATScenario
              id="INV-UAT-004"
              title="Notification Preferences Update"
              preconditions={['User is logged in']}
              steps={[
                'Navigate to Account → Notification Preferences',
                'Toggle off "Distribution Payments" email notification',
                'Save preferences',
                'Return to preferences page',
              ]}
              expected="Preferences saved. Distribution Payments toggle remains off on reload."
              status="pending"
            />
          </div>
        ),
      },
    ],
  },

  // ── ISSUER ────────────────────────────────────────────────────────────────
  {
    id: 'issuer',
    label: 'Issuer',
    icon: Building2,
    color: 'text-info',
    description: 'Capital-raising entity that creates, manages, and monitors investment campaigns on the platform.',
    portalUrl: '/issuer-portal',
    sections: [
      {
        id: 'iss-overview',
        title: 'Role Overview',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">Issuers are companies or entities raising capital through the CrowdVault platform. They create campaigns, upload disclosure documents, track funding progress, manage investor relationships, and receive settlement proceeds after campaign close.</p>
            <FeatureTable rows={[
              { feature: 'Issuer Dashboard', description: 'Campaign funding progress, investor count, escrow balance, net proceeds', access: 'Full Access' },
              { feature: 'Campaign Portal', description: 'Create, edit, and submit campaigns through the approval workflow', access: 'Full Access' },
              { feature: 'Investor List', description: 'View committed investors, KYC status, and payment status', access: 'Read-Only' },
              { feature: 'Milestones', description: 'Track campaign milestones and report progress', access: 'Full Access' },
              { feature: 'Document Vault', description: 'Upload and manage campaign disclosure documents', access: 'Full Access' },
              { feature: 'Distributions', description: 'View distribution schedules and payment status', access: 'Read-Only' },
            ]} />
          </div>
        ),
      },
      {
        id: 'iss-campaign',
        title: 'Creating & Submitting a Campaign',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">Navigate to <strong className="text-foreground">Campaigns → Campaign Portal</strong> to create a new campaign.</p>
            <StepList steps={[
              { num: 1, title: 'Create New Campaign', desc: 'Click "+ New Campaign". Enter campaign name, target amount, minimum/maximum investment, sector, jurisdiction, and offering type.' },
              { num: 2, title: 'Add Campaign Description', desc: 'Provide a detailed description of the investment opportunity, use of proceeds, and risk factors.' },
              { num: 3, title: 'Upload Disclosure Documents', desc: 'Upload required documents: Information Memorandum, Financial Statements, Legal Opinion, and any NDA-gated materials. Set access level for each document.' },
              { num: 4, title: 'Define Milestones', desc: 'Add campaign milestones with target dates and completion criteria. These are visible to investors.' },
              { num: 5, title: 'Submit for Compliance Review', desc: 'Click "Submit for Review". The campaign enters the Compliance Review stage. You cannot edit the campaign while it is under review.' },
              { num: 6, title: 'Respond to Review Notes', desc: 'Compliance, Risk, and Finance Officers may add review notes requesting changes. Address each note and resubmit.' },
              { num: 7, title: 'Campaign Published', desc: 'Once all reviews are approved, the campaign is published and visible to eligible investors.' },
            ]} />
            <InfoBox type="warning">Campaigns go through a multi-stage approval workflow: Draft → Compliance Review → Risk Review → Finance Review → Approved → Published. Each stage may require document updates.</InfoBox>
          </div>
        ),
      },
      {
        id: 'iss-monitoring',
        title: 'Monitoring a Live Campaign',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">The Issuer Dashboard provides real-time visibility into your campaign performance.</p>
            <FeatureTable rows={[
              { feature: 'Funding Progress Chart', description: 'Visual representation of funds raised vs target over time', access: 'Read-Only' },
              { feature: 'Investor Tab', description: 'Full list of committed investors with amount, KYC status, and payment status', access: 'Read-Only' },
              { feature: 'Milestones Tab', description: 'Update milestone completion percentages and status', access: 'Full Access' },
              { feature: 'Vault Tab', description: 'Manage campaign documents, set access levels, and NDA requirements', access: 'Full Access' },
              { feature: 'Workflow Stages', description: 'Visual pipeline showing current approval stage', access: 'Read-Only' },
            ]} />
            <InfoBox type="info">Escrow balance is held by the custodian until the campaign closes and all compliance conditions are met. Net proceeds = Escrow Balance minus Platform Fees.</InfoBox>
          </div>
        ),
      },
      {
        id: 'iss-uat',
        title: 'UAT Test Scenarios',
        content: (
          <div className="space-y-2">
            <UATScenario
              id="ISS-UAT-001"
              title="Create and Submit Campaign — Happy Path"
              preconditions={['Issuer account is active and KYB approved', 'At least one document ready for upload']}
              steps={[
                'Navigate to Campaigns → Campaign Portal',
                'Click "+ New Campaign"',
                'Fill all required fields (name, target, sector, jurisdiction)',
                'Upload Information Memorandum (set access: Public)',
                'Upload Financial Statements (set access: NDA-Gated)',
                'Add 3 milestones with target dates',
                'Click "Submit for Review"',
              ]}
              expected="Campaign status changes to 'Compliance Review'. Compliance Officer receives notification. Campaign appears in review queue."
              status="pending"
            />
            <UATScenario
              id="ISS-UAT-002"
              title="Update Milestone Progress"
              preconditions={['Campaign is in Published or Active status', 'At least one milestone exists']}
              steps={[
                'Navigate to Issuer Dashboard → Milestones tab',
                'Select a milestone with status "in-progress"',
                'Update completion percentage to 75%',
                'Save changes',
              ]}
              expected="Milestone completion updates to 75%. Investors with notification preferences enabled receive an update."
              status="pending"
            />
            <UATScenario
              id="ISS-UAT-003"
              title="Document Access Level Management"
              preconditions={['Campaign has at least one uploaded document']}
              steps={[
                'Navigate to Issuer Dashboard → Vault tab',
                'Select a document currently set to "Public"',
                'Change access level to "NDA-Gated"',
                'Save changes',
              ]}
              expected="Document access level updated. Investors without signed NDA can no longer download the document."
              status="pending"
            />
          </div>
        ),
      },
    ],
  },

  // ── COMPLIANCE OFFICER ────────────────────────────────────────────────────
  {
    id: 'compliance',
    label: 'Compliance Officer',
    icon: Shield,
    color: 'text-positive',
    description: 'Responsible for KYC/KYB review, AML monitoring, sanctions screening, STR filing, and regulatory reporting.',
    portalUrl: '/compliance-portal',
    sections: [
      {
        id: 'co-overview',
        title: 'Role Overview',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">The Compliance Officer is the platform's primary gatekeeper for regulatory compliance. This role manages the full KYC/KYB lifecycle, monitors AML alerts, reviews sanctions screening hits, files Suspicious Transaction Reports (STRs), and produces regulatory reports.</p>
            <FeatureTable rows={[
              { feature: 'Compliance Dashboard', description: 'KYC queue, AML alerts, EDD cases, compliance holds, approval rate metrics', access: 'Full Access' },
              { feature: 'KYC/KYB Intake', description: 'Receive and triage new KYC/KYB applications', access: 'Full Access' },
              { feature: 'KYC/KYB Approval', description: 'Approve, reject, or escalate KYC applications', access: 'Action Required' },
              { feature: 'KYC/KYB Review', description: 'Detailed review of submitted documents and risk assessment', access: 'Full Access' },
              { feature: 'AML Monitoring', description: 'Real-time AML alert feed with rule codes and severity levels', access: 'Full Access' },
              { feature: 'Screening Hits', description: 'Review PEP and sanctions screening hits', access: 'Action Required' },
              { feature: 'Campaign Review', description: 'Review and approve/reject campaign submissions', access: 'Action Required' },
              { feature: 'Email Dispatch', description: 'Send compliance notifications to investors and issuers', access: 'Full Access' },
              { feature: 'Compliance Reporting Suite', description: 'Generate and export regulatory reports', access: 'Full Access' },
              { feature: 'Event Subscriptions', description: 'Configure real-time compliance event webhooks', access: 'Full Access' },
              { feature: 'Config Dashboard', description: 'Manage compliance rules, thresholds, and workflow settings', access: 'Full Access' },
              { feature: 'Job Monitor', description: 'Monitor automated compliance job execution status', access: 'Read-Only' },
            ]} />
          </div>
        ),
      },
      {
        id: 'co-kyc',title: 'KYC/KYB Review Workflow',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">Navigate to <strong className="text-foreground">Review Queues → KYC/KYB Review</strong> to process applications.</p>
            <StepList steps={[
              { num: 1, title: 'Triage the Queue', desc: 'Applications are sorted by submission date and risk level. High-risk applications are flagged with a red badge.' },
              { num: 2, title: 'Open Application', desc: 'Click an application to view full details: personal info, uploaded documents, liveness check result, and suitability score.' },
              { num: 3, title: 'Document Verification', desc: 'Verify each uploaded document is authentic, legible, and matches the applicant profile. Mark each document as Verified or Rejected.' },
              { num: 4, title: 'Risk Assessment', desc: 'Review the system-generated risk score. Check for PEP status, adverse media, and sanctions hits.' },
              { num: 5, title: 'Assign Risk Level', desc: 'Set the applicant risk level: Low, Medium, or High. High-risk triggers Enhanced Due Diligence (EDD).' },
              { num: 6, title: 'Approve or Reject', desc: 'Approve the application (investor can now invest), reject with reason (investor is notified), or escalate to EDD.' },
              { num: 7, title: 'EDD Process', desc: 'For EDD cases, request additional documentation, conduct source of funds verification, and obtain MLRO sign-off.' },
            ]} />
            <InfoBox type="warning">All KYC decisions are immutably recorded in the audit log. Rejection reasons must be documented. High-risk approvals require MLRO countersignature.</InfoBox>
          </div>
        ),
      },
      {
        id: 'co-aml',title: 'AML Monitoring & STR Workflow',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">Navigate to <strong className="text-foreground">AML/CFT → AML Monitoring</strong> for real-time alert management.</p>
            <StepList steps={[
              { num: 1, title: 'Monitor Alert Feed', desc: 'The real-time feed shows all AML alerts with severity (Critical/High/Medium/Low), rule code, entity name, and amount.' },
              { num: 2, title: 'Investigate Alert', desc: 'Click an alert to view full transaction context, entity history, and linked campaigns.' },
              { num: 3, title: 'Assign Alert', desc: 'Assign the alert to a compliance analyst for investigation. Set a due date.' },
              { num: 4, title: 'Resolve or Escalate', desc: 'Mark as False Positive (with justification), Resolved, or escalate to STR/SAR workflow.' },
              { num: 5, title: 'File STR/SAR', desc: 'Navigate to STR/SAR Workflow tab. Complete the report form with transaction details, suspicion grounds, and supporting evidence.' },
              { num: 6, title: 'Place Compliance Hold', desc: 'If required, place a compliance hold on the entity to block further transactions pending investigation.' },
              { num: 7, title: 'Regulatory Reporting', desc: 'Export STR reports and AML statistics via the Compliance Reporting Suite.' },
            ]} />
          </div>
        ),
      },
      {
        id: 'co-uat',title: 'UAT Test Scenarios',
        content: (
          <div className="space-y-2">
            <UATScenario
              id="CO-UAT-001"
              title="KYC Application Approval — Low Risk"
              preconditions={['At least one KYC application in "Submitted" status', 'Application has all required documents uploaded']}
              steps={[
                'Navigate to Review Queues → KYC/KYB Review','Select a Low Risk application','Verify all documents (mark each as Verified)','Confirm risk level as Low','Click "Approve"','Enter approval notes','Confirm approval',
              ]}
              expected="Application status changes to 'Approved'. Investor receives email notification. Investor can now commit to investments."
              status="pending"
            />
            <UATScenario
              id="CO-UAT-002"
              title="AML Alert — Escalate to STR"
              preconditions={['At least one Critical AML alert exists in the monitoring feed']}
              steps={[
                'Navigate to AML/CFT → AML Monitoring','Select a Critical severity alert','Review transaction details and entity history','Click "Escalate to STR"','Complete STR form with suspicion grounds','Attach supporting evidence','Submit STR',
              ]}
              expected="STR filed and recorded in compliance log. Alert status changes to 'STR Filed'. MLRO receives notification."
              status="pending"
            />
            <UATScenario
              id="CO-UAT-003"
              title="Campaign Compliance Review"
              preconditions={['At least one campaign in "Compliance Review" stage']}
              steps={[
                'Navigate to Review Queues → Campaign Review','Select campaign in Compliance Review stage','Review all uploaded documents','Add review note requesting additional information','Set status to "Pending Issuer Response"',
              ]}
              expected="Review note visible to Issuer. Campaign status updated. Issuer receives notification to provide additional information."
              status="pending"
            />
            <UATScenario
              id="CO-UAT-004"
              title="Sanctions Screening Hit Review"
              preconditions={['At least one screening hit exists']}
              steps={[
                'Navigate to AML/CFT → Screening Hits','Select a screening hit','Review match details and entity information','Determine: True Match or False Positive','If True Match: place compliance hold and escalate','If False Positive: dismiss with documented justification',
              ]}
              expected="Screening hit resolved with documented outcome. If true match, compliance hold placed and entity blocked from transactions."
              status="pending"
            />
          </div>
        ),
      },
    ],
  },

  // ── FINANCE OFFICER ───────────────────────────────────────────────────────
  {
    id: 'finance',label: 'Finance Officer',icon: Landmark,color: 'text-warning',description: 'Manages escrow positions, settlement processing, ledger reconciliation, and distribution management.',portalUrl: '/finance-portal',
    sections: [
      {
        id: 'fo-overview',title: 'Role Overview',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">The Finance Officer oversees all financial operations on the platform including escrow management, settlement batch processing, double-entry ledger maintenance, reconciliation, and investor distribution management.</p>
            <FeatureTable rows={[
              { feature: 'Finance Dashboard', description: 'Escrow positions, pending settlements, MTD settled, distributions due, failed transactions', access: 'Full Access' },
              { feature: 'Escrow Positions', description: 'View all escrow accounts by campaign with balance and custodian details', access: 'Read-Only' },
              { feature: 'Settlement Queue', description: 'Review and approve pending settlement batches', access: 'Action Required' },
              { feature: 'Settlement Processor', description: 'Process settlement batches with dual-authorisation workflow', access: 'Action Required' },
              { feature: 'Distribution Management', description: 'Schedule and process investor distribution payments', access: 'Full Access' },
              { feature: 'Transaction Ledger', description: 'View all platform transactions with type, amount, and status', access: 'Read-Only' },
              { feature: 'Double-Entry Ledger', description: 'Full double-entry accounting ledger with journal entries', access: 'Read-Only' },
              { feature: 'Reconciliation', description: 'Reconcile platform records against custodian statements', access: 'Full Access' },
              { feature: 'Fee Management', description: 'View and manage platform fee deductions', access: 'Read-Only' },
            ]} />
          </div>
        ),
      },
      {
        id: 'fo-settlement',title: 'Settlement Processing',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">Navigate to <strong className="text-foreground">Funds Management → Settlement Processor</strong> to process settlement batches.</p>
            <StepList steps={[
              { num: 1, title: 'Review Settlement Queue', desc: 'The queue shows all pending settlement batches with campaign, amount, investor count, priority, and submitter.' },
              { num: 2, title: 'Verify Compliance Clearance', desc: 'Confirm the campaign has received compliance sign-off before processing. Batches without compliance approval cannot be released.' },
              { num: 3, title: 'Review Batch Details', desc: 'Click a batch to view individual investor allocations, custodian details, and payment references.' },
              { num: 4, title: 'Dual Authorisation', desc: 'All escrow releases above $100,000 require dual authorisation. Finance Officer approves first; Trustee countersigns.' },
              { num: 5, title: 'Initiate Settlement', desc: 'Click "Process Settlement". Funds are released from escrow to the issuer settlement account.' },
              { num: 6, title: 'Confirm Settlement', desc: 'Verify custodian confirmation receipt. Update batch status to Settled.' },
              { num: 7, title: 'Generate Investor Receipts', desc: 'System automatically generates settlement receipts for all investors in the batch.' },
            ]} />
            <InfoBox type="warning">Never process a settlement batch without compliance sign-off. Dual authorisation is mandatory for all batches above $100,000 per FCA CASS 7 requirements.</InfoBox>
          </div>
        ),
      },
      {
        id: 'fo-reconciliation',title: 'Reconciliation',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">Navigate to <strong className="text-foreground">Ledger → Reconciliation</strong> to perform daily reconciliation.</p>
            <StepList steps={[
              { num: 1, title: 'Import Custodian Statement', desc: 'Upload the daily custodian statement (CSV or PDF). The system parses and matches entries automatically.' },
              { num: 2, title: 'Review Matched Entries', desc: 'Matched entries are shown in green. Unmatched entries require manual investigation.' },
              { num: 3, title: 'Investigate Discrepancies', desc: 'For each unmatched entry, review the transaction details and determine if it is a timing difference, error, or genuine discrepancy.' },
              { num: 4, title: 'Resolve Discrepancies', desc: 'Mark timing differences as "Pending Next Day". Genuine discrepancies must be escalated to the Trustee and Auditor.' },
              { num: 5, title: 'Sign Off Reconciliation', desc: 'Once all entries are resolved, sign off the daily reconciliation. This is recorded in the audit log.' },
            ]} />
            <InfoBox type="info">Reconciliation must be completed by T+1 (next business day) per FCA CASS 7.15. Outstanding discrepancies older than T+1 are automatically flagged to the Auditor.</InfoBox>
          </div>
        ),
      },
      {
        id: 'fo-uat',title: 'UAT Test Scenarios',
        content: (
          <div className="space-y-2">
            <UATScenario
              id="FO-UAT-001"
              title="Settlement Batch Processing — Happy Path"
              preconditions={['At least one settlement batch in "Pending" status', 'Campaign has compliance sign-off', 'Trustee has countersigned']}
              steps={[
                'Navigate to Funds Management → Settlement Processor','Select a Pending batch with compliance clearance','Review batch details and investor allocations','Click "Process Settlement"','Confirm dual authorisation is complete','Initiate settlement','Verify custodian confirmation',
              ]}
              expected="Batch status changes to 'Settled'. Investor receipts generated. Ledger updated with settlement entries."
              status="pending"
            />
            <UATScenario
              id="FO-UAT-002"
              title="Distribution Payment Processing"
              preconditions={['At least one campaign with matured distributions', 'Investor bank details on file']}
              steps={[
                'Navigate to Funds Management → Distributions','Select a distribution batch due for payment','Review investor allocation list','Verify total distribution amount matches ledger','Initiate distribution payment','Confirm payment references',
              ]}
              expected="Distribution payments initiated. Investors receive payment notifications. Distribution records updated in ledger."
              status="pending"
            />
            <UATScenario
              id="FO-UAT-003"
              title="Daily Reconciliation — Discrepancy Detection"
              preconditions={['Custodian statement available for import', 'At least one transaction in the ledger']}
              steps={[
                'Navigate to Ledger → Reconciliation','Import custodian statement','Review auto-matched entries','Identify any unmatched entries','Investigate discrepancy details','Escalate genuine discrepancy to Trustee',
              ]}
              expected="Discrepancy flagged and escalated. Trustee receives notification. Reconciliation status shows 'Pending Resolution'."
              status="pending"
            />
          </div>
        ),
      },
    ],
  },

  // ── RISK OFFICER ──────────────────────────────────────────────────────────
  {
    id: 'risk',label: 'Risk Officer',icon: BarChart3,color: 'text-critical',description: 'Monitors portfolio risk scores, exposure concentration, limit breaches, and campaign risk assessments.',portalUrl: '/risk-portal',
    sections: [
      {
        id: 'ro-overview',title: 'Role Overview',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">The Risk Officer monitors and manages all risk dimensions across the platform including entity risk scores, portfolio concentration, geographic exposure, limit breaches, and campaign risk assessments.</p>
            <FeatureTable rows={[
              { feature: 'Risk Dashboard', description: 'Critical entities, high-risk count, limit breaches, avg risk score, portfolio VaR', access: 'Full Access' },
              { feature: 'Risk Scores', description: 'Entity-level risk scores with trend indicators and exposure amounts', access: 'Read-Only' },
              { feature: 'Risk Assessment', description: 'Conduct detailed risk assessments for campaigns and entities', access: 'Full Access' },
              { feature: 'Flagged Entities', description: 'List of entities flagged for elevated risk with trigger details', access: 'Full Access' },
              { feature: 'Limit Breaches', description: 'Active limit breaches with severity and recommended actions', access: 'Action Required' },
              { feature: 'Geographic Exposure', description: 'Portfolio exposure by jurisdiction and asset class', access: 'Read-Only' },
              { feature: 'Concentration Analysis', description: 'Portfolio concentration risk by sector, issuer, and geography', access: 'Read-Only' },
              { feature: 'Risk Reports', description: 'Generate and export risk reports', access: 'Full Access' },
            ]} />
          </div>
        ),
      },
      {
        id: 'ro-monitoring',title: 'Risk Monitoring Workflow',
        content: (
          <div>
            <StepList steps={[
              { num: 1, title: 'Daily Dashboard Review', desc: 'Check the Risk Dashboard for new critical entities, limit breaches, and flagged entities. The real-time feed shows intraday risk events.' },
              { num: 2, title: 'Investigate Flagged Entities', desc: 'Navigate to Flagged Entities tab. Review risk score, exposure amount, risk trend (improving/deteriorating), and trigger events.' },
              { num: 3, title: 'Limit Breach Response', desc: 'Navigate to Limit Breaches tab. For each breach, review severity, current vs limit value, and recommended action. Escalate critical breaches to MLRO.' },
              { num: 4, title: 'Campaign Risk Assessment', desc: 'Navigate to Risk Assessment. Conduct structured risk assessment for campaigns in the approval pipeline. Score across credit, market, liquidity, and operational risk dimensions.' },
              { num: 5, title: 'Concentration Review', desc: 'Review portfolio concentration charts. Flag any sector or geographic concentration above policy limits.' },
              { num: 6, title: 'Risk Report Generation', desc: 'Generate weekly and monthly risk reports for the Risk Committee. Export as PDF or CSV.' },
            ]} />
            <InfoBox type="warning">Limit breaches with severity "Critical" must be escalated to the MLRO and Risk Committee within 24 hours. Document all escalations in the audit trail.</InfoBox>
          </div>
        ),
      },
      {
        id: 'ro-uat',title: 'UAT Test Scenarios',
        content: (
          <div className="space-y-2">
            <UATScenario
              id="RO-UAT-001"
              title="Limit Breach Escalation"
              preconditions={['At least one Critical limit breach exists in the system']}
              steps={[
                'Navigate to Risk Portal → Limit Breaches tab','Select a Critical severity breach','Review breach details: current value, limit, breach percentage','Review recommended action','Click "Escalate to MLRO"','Add escalation notes','Confirm escalation',
              ]}
              expected="Breach escalated. MLRO receives notification. Breach status updated to 'Escalated'. Audit log entry created."
              status="pending"
            />
            <UATScenario
              id="RO-UAT-002"
              title="Campaign Risk Assessment"
              preconditions={['At least one campaign in Risk Review stage']}
              steps={[
                'Navigate to Risk Assessment','Select campaign pending risk review','Complete risk scoring across all dimensions','Add risk assessment notes','Set overall risk rating (Low/Medium/High)','Submit assessment',
              ]}
              expected="Risk assessment recorded. Campaign workflow advances to Finance Review stage. Issuer notified of risk rating."
              status="pending"
            />
          </div>
        ),
      },
    ],
  },

  // ── AUDITOR ───────────────────────────────────────────────────────────────
  {
    id: 'auditor',label: 'Auditor',icon: Eye,color: 'text-accent',description: 'Independent read-only access to transaction logs, settlement verification, compliance controls, and cryptographic proofs.',portalUrl: '/auditor-portal',
    sections: [
      {
        id: 'au-overview',title: 'Role Overview',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">The Auditor has read-only access to all financial records, compliance controls, and cryptographic proofs. This role is designed for internal and external auditors who need to verify the integrity of the platform's financial and compliance operations without the ability to modify any records.</p>
            <FeatureTable rows={[
              { feature: 'Transaction Logs', description: 'Full transaction history with AML flags, journal references, and custodian details', access: 'Read-Only' },
              { feature: 'Settlement Verification', description: 'Verify settlement batches against custodian and bank records', access: 'Read-Only' },
              { feature: 'Compliance Controls', description: 'Review control test results, findings, and evidence', access: 'Read-Only' },
              { feature: 'Cryptographic Proofs', description: 'Verify SHA-256 hash chain integrity for all ledger entries', access: 'Read-Only' },
              { feature: 'Double-Entry Ledger', description: 'Full double-entry accounting ledger view', access: 'Read-Only' },
              { feature: 'Reconciliation', description: 'View reconciliation status and discrepancy reports', access: 'Read-Only' },
              { feature: 'Document Vault', description: 'Access all platform documents for audit purposes', access: 'Read-Only' },
              { feature: 'Audit Trail', description: 'Immutable log of all platform actions and events', access: 'Read-Only' },
            ]} />
          </div>
        ),
      },
      {
        id: 'au-workflow',
        title: 'Audit Workflow',
        content: (
          <div>
            <StepList steps={[
              { num: 1, title: 'Transaction Log Review', desc: 'Navigate to Auditor Portal → Transaction Logs. Filter by date range, transaction type, or AML flag status. Export to CSV for offline analysis.' },
              { num: 2, title: 'Settlement Verification', desc: 'Navigate to Settlement Verification tab. For each batch, verify custodian confirmation, bank confirmation, and regulatory reporting status.' },
              { num: 3, title: 'Compliance Control Testing', desc: 'Navigate to Compliance Controls tab. Review each control\'s last test date, test result (Pass/Fail/Warning), evidence, and findings.' },
              { num: 4, title: 'Cryptographic Proof Verification', desc: 'Navigate to Cryptographic Proofs tab. Verify the SHA-256 hash chain is intact. Any broken chain link indicates potential tampering.' },
              { num: 5, title: 'Ledger Integrity Check', desc: 'Navigate to Double-Entry Ledger. Verify all debits equal credits. Check for any manual journal entries above $50,000 and confirm they have Finance Officer approval.' },
              { num: 6, title: 'Reconciliation Review', desc: 'Navigate to Reconciliation. Verify all batches are reconciled within T+1. Flag any outstanding discrepancies.' },
              { num: 7, title: 'Export Audit Evidence', desc: 'Use the Download button on each section to export evidence packages for the audit file.' },
            ]} />
            <InfoBox type="tip">The Cryptographic Proofs tab shows the SHA-256 hash chain for all ledger entries. A "Valid" status with "Chain Intact: Yes" confirms no tampering has occurred. Any "Invalid" status must be immediately escalated.</InfoBox>
          </div>
        ),
      },
      {
        id: 'au-uat',
        title: 'UAT Test Scenarios',
        content: (
          <div className="space-y-2">
            <UATScenario
              id="AU-UAT-001"
              title="Transaction Log Export"
              preconditions={['Auditor is logged in', 'At least 10 transactions exist in the system']}
              steps={[
                'Navigate to Auditor Portal → Transaction Logs tab',
                'Set date filter to last 30 days',
                'Filter by "AML Flagged" transactions',
                'Review flagged transaction details',
                'Click "Download" to export CSV',
              ]}
              expected="CSV file downloaded containing all AML-flagged transactions in the selected date range with full details."
              status="pending"
            />
            <UATScenario
              id="AU-UAT-002"
              title="Cryptographic Hash Chain Verification"
              preconditions={['At least 5 ledger entries exist']}
              steps={[
                'Navigate to Auditor Portal → Cryptographic Proofs tab',
                'Review the most recent 5 entries',
                'Verify each entry shows "Valid" proof status',
                'Verify "Chain Intact: Yes" for each entry',
                'Check that prevHash of entry N matches integrityHash of entry N-1',
              ]}
              expected="All entries show Valid status with intact chain. Hash values are consistent across sequential entries."
              status="pending"
            />
            <UATScenario
              id="AU-UAT-003"
              title="Compliance Control Review — Failed Control"
              preconditions={['At least one compliance control with "Fail" status exists']}
              steps={[
                'Navigate to Auditor Portal → Compliance Controls tab',
                'Filter by Status: Fail',
                'Select the failed control',
                'Review findings and evidence',
                'Note the next test date',
              ]}
              expected="Failed control details visible with findings, evidence reference, and remediation timeline. Auditor can download evidence."
              status="pending"
            />
          </div>
        ),
      },
    ],
  },

  // ── TRUSTEE ───────────────────────────────────────────────────────────────
  {
    id: 'trustee',
    label: 'Trustee',
    icon: Landmark,
    color: 'text-info',
    description: 'Independent oversight of escrow positions, settlement authorisation, custody verification, and portfolio integrity.',
    portalUrl: '/trustee-portal',
    sections: [
      {
        id: 'tr-overview',
        title: 'Role Overview',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">The Trustee provides independent oversight of all client money held in escrow. This role is responsible for countersigning settlement releases, verifying custody records, and ensuring all escrow positions are accurately maintained.</p>
            <FeatureTable rows={[
              { feature: 'Portfolio Oversight', description: 'View all campaign holdings with NAV, unrealised gains, and custodian details', access: 'Read-Only' },
              { feature: 'Escrow Oversight', description: 'Monitor all escrow positions with balance, release conditions, and status', access: 'Action Required' },
              { feature: 'Settlement Review', description: 'Review and countersign settlement batches', access: 'Action Required' },
              { feature: 'Custody Verification', description: 'Verify custodian records against platform records', access: 'Full Access' },
              { feature: 'Document Vault', description: 'Access all trust and custody documents', access: 'Read-Only' },
              { feature: 'Audit Trail', description: 'View immutable audit log of all trustee actions', access: 'Read-Only' },
            ]} />
          </div>
        ),
      },
      {
        id: 'tr-workflow',
        title: 'Trustee Oversight Workflow',
        content: (
          <div>
            <StepList steps={[
              { num: 1, title: 'Daily Escrow Review', desc: 'Navigate to Trustee Portal → Escrow Oversight. Review all active escrow positions. Verify balances match custodian records.' },
              { num: 2, title: 'Settlement Countersignature', desc: 'Navigate to Settlement Review tab. Review pending settlement batches. Verify compliance sign-off is present. Countersign to authorise release.' },
              { num: 3, title: 'Custody Verification', desc: 'Navigate to Custody Verification tab. For each asset, verify nominal value matches verified value. Investigate any variance.' },
              { num: 4, title: 'Disputed Escrow Management', desc: 'For escrow positions with "Disputed" status, review dispute details and coordinate with legal team. Do not release disputed funds.' },
              { num: 5, title: 'Release Condition Monitoring', desc: 'Monitor escrow positions approaching their release date. Verify all release conditions are met before countersigning.' },
            ]} />
            <InfoBox type="warning">Never countersign a settlement batch without verifying: (1) Compliance sign-off is present, (2) All release conditions are met, (3) Custodian balance matches the batch amount.</InfoBox>
          </div>
        ),
      },
      {
        id: 'tr-uat',
        title: 'UAT Test Scenarios',
        content: (
          <div className="space-y-2">
            <UATScenario
              id="TR-UAT-001"
              title="Settlement Countersignature"
              preconditions={['At least one settlement batch in "Pending" status', 'Finance Officer has approved the batch', 'Compliance sign-off is present']}
              steps={[
                'Navigate to Trustee Portal → Settlement Review tab',
                'Select a Pending batch',
                'Verify compliance sign-off indicator is green',
                'Review batch details and custodian reference',
                'Click "Countersign"',
                'Enter trustee authorisation code',
                'Confirm countersignature',
              ]}
              expected="Batch status changes to 'Processing'. Finance Officer notified. Settlement proceeds to execution."
              status="pending"
            />
            <UATScenario
              id="TR-UAT-002"
              title="Custody Discrepancy Investigation"
              preconditions={['At least one custody record with "Discrepancy" status']}
              steps={[
                'Navigate to Trustee Portal → Custody Verification tab',
                'Select record with Discrepancy status',
                'Review nominal value vs verified value variance',
                'Click "Investigate"',
                'Add investigation notes',
                'Escalate to Auditor',
              ]}
              expected="Discrepancy escalated to Auditor. Audit log entry created. Custodian notified for clarification."
              status="pending"
            />
          </div>
        ),
      },
    ],
  },

  // ── PLATFORM ADMIN ────────────────────────────────────────────────────────
  {
    id: 'admin',
    label: 'Platform Admin',
    icon: Settings,
    color: 'text-muted',
    description: 'Full platform administration including user management, tenant configuration, campaign approvals, and system monitoring.',
    portalUrl: '/platform-administration-portal',
    sections: [
      {
        id: 'pa-overview',
        title: 'Role Overview',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">The Platform Administrator has the highest level of access across the entire CrowdVault platform. This role manages users, tenants, RBAC permissions, system health, and has override capabilities for all workflows.</p>
            <FeatureTable rows={[
              { feature: 'Admin Dashboard', description: 'Platform AUM, active tenants, campaigns, investors, pending approvals, system health', access: 'Full Access' },
              { feature: 'Tenant Management', description: 'Create and manage tenant organisations on the platform', access: 'Full Access' },
              { feature: 'User Provisioning', description: 'Create, edit, deactivate users and assign roles', access: 'Full Access' },
              { feature: 'Role Matrix', description: 'View and configure role-based access control matrix', access: 'Full Access' },
              { feature: 'Permission Overrides', description: 'Grant temporary permission overrides for specific users', access: 'Full Access' },
              { feature: 'Session Controls', description: 'View and terminate active user sessions', access: 'Full Access' },
              { feature: 'Campaign Approval', description: 'Final approval authority for all campaigns', access: 'Action Required' },
              { feature: 'Audit Logs', description: 'Full platform audit log with all user actions', access: 'Read-Only' },
              { feature: 'System Health', description: 'Monitor all platform services, latency, and uptime', access: 'Read-Only' },
              { feature: 'RBAC & Roles', description: 'Configure role permissions and access policies', access: 'Full Access' },
            ]} />
          </div>
        ),
      },
      {
        id: 'pa-users',
        title: 'User Management',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">Navigate to <strong className="text-foreground">User Management → User Provisioning</strong> to manage platform users.</p>
            <StepList steps={[
              { num: 1, title: 'Create New User', desc: 'Click "+ New User". Enter email, full name, and select role (investor, issuer, compliance, finance, risk, auditor, trustee, admin).' },
              { num: 2, title: 'Assign Tenant', desc: 'Assign the user to the appropriate tenant organisation. Multi-tenant users can be assigned to multiple tenants.' },
              { num: 3, title: 'Set Permissions', desc: 'Review the default permissions for the selected role. Apply any permission overrides if required.' },
              { num: 4, title: 'Send Invitation', desc: 'Click "Send Invitation". User receives an email with a secure onboarding link valid for 48 hours.' },
              { num: 5, title: 'Manage Active Sessions', desc: 'Navigate to Session Controls to view all active sessions. Terminate suspicious sessions immediately.' },
              { num: 6, title: 'Deactivate User', desc: 'To deactivate a user, click their profile and toggle "Active" to off. All active sessions are immediately terminated.' },
            ]} />
            <InfoBox type="warning">Admin role assignment requires dual approval from two existing Platform Admins. This prevents privilege escalation attacks.</InfoBox>
          </div>
        ),
      },
      {
        id: 'pa-system',
        title: 'System Health Monitoring',
        content: (
          <div>
            <p className="text-sm text-muted mb-3">Navigate to <strong className="text-foreground">System → System Health</strong> to monitor platform services.</p>
            <FeatureTable rows={[
              { feature: 'API Gateway', description: 'Core API routing service — target latency <100ms', access: 'Read-Only' },
              { feature: 'Auth Service', description: 'Authentication and session management — target latency <50ms', access: 'Read-Only' },
              { feature: 'Escrow Engine', description: 'Escrow calculation and management — target latency <200ms', access: 'Read-Only' },
              { feature: 'KYC/AML Service', description: 'Identity verification and AML screening — target latency <500ms', access: 'Read-Only' },
              { feature: 'Document Vault', description: 'Document storage and retrieval — target latency <300ms', access: 'Read-Only' },
              { feature: 'Notification Service', description: 'Email and in-app notification delivery', access: 'Read-Only' },
              { feature: 'Webhook Dispatcher', description: 'Outbound webhook delivery for compliance events', access: 'Read-Only' },
            ]} />
            <InfoBox type="info">Services showing "Degraded" status require immediate investigation. Contact the infrastructure team if latency exceeds 2x the target for more than 5 minutes.</InfoBox>
          </div>
        ),
      },
      {
        id: 'pa-uat',
        title: 'UAT Test Scenarios',
        content: (
          <div className="space-y-2">
            <UATScenario
              id="PA-UAT-001"
              title="New User Provisioning — Compliance Officer Role"
              preconditions={['Admin is logged in', 'Target tenant exists in the system']}
              steps={[
                'Navigate to User Management → User Provisioning',
                'Click "+ New User"',
                'Enter email: test.compliance@crowdvault.test',
                'Enter full name and select role: Compliance Officer',
                'Assign to target tenant',
                'Review default permissions',
                'Click "Send Invitation"',
              ]}
              expected="User created with Compliance Officer role. Invitation email sent. User appears in user list with status 'Invited'."
              status="pending"
            />
            <UATScenario
              id="PA-UAT-002"
              title="Campaign Final Approval"
              preconditions={['At least one campaign has passed Compliance, Risk, and Finance reviews']}
              steps={[
                'Navigate to Security → Campaign Approval',
                'Select campaign with all reviews approved',
                'Review campaign summary and review notes',
                'Click "Final Approve"',
                'Enter approval justification',
                'Confirm approval',
              ]}
              expected="Campaign status changes to 'Approved'. Issuer notified. Campaign scheduled for publication."
              status="pending"
            />
            <UATScenario
              id="PA-UAT-003"
              title="Session Termination — Suspicious Activity"
              preconditions={['At least one active user session exists']}
              steps={[
                'Navigate to User Management → Session Controls',
                'Identify suspicious session (unusual IP or location)',
                'Click "Terminate Session"',
                'Confirm termination',
                'Verify session removed from active list',
              ]}
              expected="Session terminated immediately. User is logged out. Audit log entry created with admin ID and reason."
              status="pending"
            />
            <UATScenario
              id="PA-UAT-004"
              title="Permission Override — Temporary Access Grant"
              preconditions={['Target user exists with standard role permissions']}
              steps={[
                'Navigate to User Management → Permission Overrides',
                'Select target user',
                'Add override: grant "Export Audit Logs" permission',
                'Set expiry: 24 hours',
                'Add justification note',
                'Save override',
              ]}
              expected="Override applied. User can now export audit logs for 24 hours. Override appears in audit log. Auto-expires after 24 hours."
              status="pending"
            />
          </div>
        ),
      },
    ],
  },
];

// ─── Main Component ───────────────────────────────────────────────────────────
export default function DocumentationPage() {
  const [activeRole, setActiveRole] = useState<string>('investor');
  const [activeSection, setActiveSection] = useState<string>('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const currentRole = roles.find((r) => r.id === activeRole) || roles[0];

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const isSectionOpen = (sectionId: string) => expandedSections[sectionId] !== false;

  return (
    <div className="min-h-screen bg-surface text-foreground flex flex-col">
      {/* ── Header ── */}
      <header className="sticky top-0 z-40 bg-surface-2 border-b border-border px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-surface-3 text-muted"
          >
            <Menu size={18} />
          </button>
          <div className="flex items-center gap-2">
            <BookOpen size={20} className="text-accent" />
            <div>
              <h1 className="text-sm font-bold text-foreground leading-tight">CrowdVault™ Documentation</h1>
              <p className="text-xs text-muted">UAT Guide & User Manual — v2.0 — August 2026</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link href="/" className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5">
            <ArrowRight size={12} />
            Back to App
          </Link>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* ── Left Sidebar — Role Selector ── */}
        <aside className={`
          fixed inset-y-0 left-0 z-30 w-64 bg-surface-2 border-r border-border flex flex-col pt-16 transition-transform duration-300
          lg:static lg:translate-x-0 lg:pt-0 lg:w-56 lg:flex-shrink-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          {/* Mobile close */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border lg:hidden">
            <span className="text-xs font-semibold text-muted uppercase tracking-wider">User Roles</span>
            <button onClick={() => setSidebarOpen(false)} className="p-1 text-muted hover:text-foreground">
              <X size={16} />
            </button>
          </div>

          <div className="p-3 border-b border-border hidden lg:block">
            <p className="text-xs font-semibold text-muted uppercase tracking-wider px-2 mb-2">User Roles</p>
          </div>

          <nav className="flex-1 overflow-y-auto p-2">
            {roles.map((role) => {
              const RoleIcon = role.icon;
              return (
                <button
                  key={role.id}
                  onClick={() => { setActiveRole(role.id); setSidebarOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors mb-0.5 ${
                    activeRole === role.id
                      ? 'bg-accent/10 text-accent' :'text-muted hover:bg-surface-3 hover:text-foreground'
                  }`}
                >
                  <RoleIcon size={15} className="flex-shrink-0" />
                  <span className="text-sm font-medium">{role.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Legend */}
          <div className="p-3 border-t border-border">
            <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">Access Legend</p>
            <div className="space-y-1">
              {[
                { label: 'Full Access', color: 'bg-positive/10 text-positive' },
                { label: 'Read-Only', color: 'bg-info/10 text-info' },
                { label: 'Action Required', color: 'bg-warning/10 text-warning' },
              ].map((l) => (
                <div key={l.label} className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${l.color}`}>{l.label}</span>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-20 bg-black/50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* ── Main Content ── */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-6 lg:px-8">

            {/* Role Header */}
            <div className="mb-6 p-5 rounded-xl bg-surface-2 border border-border">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl bg-surface-3 flex items-center justify-center flex-shrink-0 ${currentRole.color}`}>
                  <currentRole.icon size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-bold text-foreground">{currentRole.label}</h2>
                    <span className="text-xs text-muted bg-surface-3 px-2 py-0.5 rounded-full">Portal Role</span>
                  </div>
                  <p className="text-sm text-muted mt-1">{currentRole.description}</p>
                  <div className="flex items-center gap-2 mt-3">
                    <Link
                      href={currentRole.portalUrl}
                      className="inline-flex items-center gap-1.5 text-xs text-accent hover:text-accent/80 bg-accent/10 px-3 py-1.5 rounded-lg transition-colors"
                    >
                      <ExternalLink size={11} />
                      Open {currentRole.label} Portal
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Sections */}
            <div className="space-y-3">
              {currentRole.sections.map((section) => (
                <div key={section.id} className="rounded-xl border border-border overflow-hidden">
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between px-5 py-4 bg-surface-2 hover:bg-surface-2/80 text-left"
                  >
                    <h3 className="text-sm font-semibold text-foreground">{section.title}</h3>
                    {isSectionOpen(section.id)
                      ? <ChevronUp size={16} className="text-muted flex-shrink-0" />
                      : <ChevronDown size={16} className="text-muted flex-shrink-0" />
                    }
                  </button>
                  {isSectionOpen(section.id) && (
                    <div className="px-5 py-4 bg-surface border-t border-border">
                      {section.content}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Platform-wide section */}
            {activeRole === 'admin' && (
              <div className="mt-6 p-5 rounded-xl bg-surface-2 border border-border">
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Zap size={14} className="text-warning" />
                  Platform-Wide UAT Checklist
                </h3>
                <div className="space-y-2">
                  {[
                    'All 8 user roles can log in successfully',
                    'KYC submission flow completes end-to-end for both Individual and Institutional',
                    'Campaign creation → approval workflow completes all 5 stages',
                    'Investment commitment flow completes with escrow receipt generation',
                    'Settlement batch processes with dual authorisation (Finance + Trustee)',
                    'AML alert triggers and escalation to STR workflow',
                    'Cryptographic hash chain validates across all ledger entries',
                    'Reconciliation detects and flags discrepancies correctly',
                    'Audit log captures all actions with correct actor, timestamp, and IP',
                    'Real-time alerts deliver to correct role portals within 5 seconds',
                    'Document vault access controls enforce NDA-gating correctly',
                    'Session termination immediately logs out the target user',
                    'Permission overrides expire automatically at configured time',
                    'All data exports (CSV/PDF) generate correctly with complete data',
                    'Mobile responsive layout works on 375px viewport for all portals',
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <div className="w-4 h-4 rounded border border-border flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-muted">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-border text-center">
              <p className="text-xs text-muted">CrowdVault™ Platform Documentation — Version 2.0</p>
              <p className="text-xs text-muted mt-1">For support, contact: <span className="text-accent">support@crowdvault.my</span></p>
              <p className="text-xs text-muted mt-1">Published URL: <span className="text-accent">https://crowdvault.digitas.my</span></p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
